import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraSwimlaneTimeline, swimlaneScale } from '@mono/ui-components/captains';
import { chartHatchBackground, SpecraBadge, SpecraLink } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraRestrictedChip, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { registryHref } from '../../registryHref';
import { formatVehicleTime, fractionalYear, monthYear } from './vehicleUi';
const title = 'Task orders we track on it · who held each round';
/** Where a round's bar goes: a competitor's company, or our program once the round carries one. */ function roundHref(round) {
    if (round.holderKind === 'US' && round.programId) return registryHref({
        kind: 'PROGRAM',
        id: round.programId
    }) ?? undefined;
    if (round.holderKind !== 'COMPETITOR' || !round.holder?.id) return undefined;
    return registryHref({
        kind: 'COMPANY',
        id: round.holder.id
    }) ?? undefined;
}
export function roundSegments(round, roundLabel = 'Round') {
    if (!round.start || !round.end) return [];
    const ours = round.holderKind === 'US';
    const tone = ours ? 'ours' : round.status === 'PRIOR' || round.holderKind === 'PRIOR' ? 'legacy' : 'theirs';
    const holder = ours ? 'Us' : round.holder?.name || 'Holder unknown';
    const href = roundHref(round);
    const describe = (what, from, to)=>`${what} · ${holder} · ${monthYear(from)}–${monthYear(to)}`;
    return [
        {
            id: round.roundKey,
            start: fractionalYear(round.start),
            end: fractionalYear(round.end),
            label: holder,
            tone,
            href,
            title: describe(roundLabel, round.start, round.end)
        },
        ...round.optionsEnd && fractionalYear(round.optionsEnd) > fractionalYear(round.end) ? [
            {
                id: `${round.roundKey}-options`,
                start: fractionalYear(round.end),
                end: fractionalYear(round.optionsEnd),
                label: ours ? `PoP to ${monthYear(round.optionsEnd)}` : 'options',
                tone: ours ? 'ours-future' : 'theirs-future',
                href,
                title: describe(`${roundLabel} options`, round.end, round.optionsEnd)
            }
        ] : []
    ];
}
/**
 * One hatched "Ordering closed" span over every lane, from the ordering end to the right edge.
 * The chart has no overlay slot, so it is portalled into the chart's own time track, which is
 * the box the chart positions segments in (its percentages are the chart's own scale).
 */ function OrderingClosedOverlay({ host, scale, orderingEnd }) {
    if (!host) return null;
    const left = swimlaneScale(scale)(fractionalYear(orderingEnd));
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("div", {
        "aria-hidden": "true",
        className: "pointer-events-none absolute bottom-0 right-0 top-[var(--component-swimlane-header-height)]",
        "data-pattern": "hatched",
        "data-testid": Testids.registryVehicle.orderingClosed,
        style: {
            left: `${left}%`,
            backgroundImage: chartHatchBackground,
            opacity: 0.6
        },
        children: /*#__PURE__*/ _jsx(SpecraBadge, {
            className: "absolute bottom-[var(--space-1)] right-[var(--space-1)] whitespace-nowrap bg-[var(--surface-card)]",
            variant: "outline",
            children: `Ordering closed ${monthYear(orderingEnd)}`
        })
    }), host);
}
/** The chart's time track (it remounts when the chart toggles to its table), found from outside. */ function useTrackHost(ref) {
    const [host, setHost] = useState(null);
    useLayoutEffect(()=>{
        const root = ref.current;
        if (!root) return;
        const find = ()=>setHost(root.querySelector('[data-chart-overlay]')?.parentElement ?? null);
        find();
        const observer = new MutationObserver(find);
        observer.observe(root, {
            childList: true,
            subtree: true
        });
        return ()=>observer.disconnect();
    }, [
        ref
    ]);
    return host;
}
/** Mockup: "Open as a landscape →" beside the section title; always shown. */ function OpenAsLandscape({ vehicleId }) {
    return /*#__PURE__*/ _jsx(SpecraLink, {
        "data-testid": Testids.registryVehicle.openAsLandscape,
        renderLink: renderRouterLink,
        href: `/landscapes/new?fromVehicle=${encodeURIComponent(vehicleId)}`,
        children: "Open as a landscape →"
    });
}
export function TaskOrderRounds({ data, dossier: d, year }) {
    const trackRef = useRef(null);
    const host = useTrackHost(trackRef);
    if (data.growthHidden || data.requirements.length === 0) return /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: title,
                action: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-2",
                    children: [
                        data.growthHidden && data.growthPresence && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "teal",
                                    children: "Delivery"
                                }),
                                /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                                    label: "Growth is working this"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(OpenAsLandscape, {
                            vehicleId: d.vehicleId
                        })
                    ]
                })
            }),
            !(data.growthHidden && data.growthPresence) && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                size: "compact",
                title: data.growthHidden ? 'Task-order history is Growth landscape data.' : 'No task orders tracked on this vehicle yet.'
            })
        ]
    });
    const orderingEnd = data.orderingEnd ?? d.orderingEnd;
    const hatchVisible = !!orderingEnd && fractionalYear(orderingEnd) < year + 3;
    const successor = d.successors.find((edge)=>edge.relation === 'SUCCESSOR' && !edge.archivedAt)?.successor;
    const lanes = data.requirements.map((requirement)=>({
            id: requirement.seatKey,
            label: requirement.name,
            sublabel: [
                requirement.customer?.name,
                requirement.valueLabel
            ].filter(Boolean).join(' · '),
            segments: [
                ...requirement.rounds.flatMap((round)=>roundSegments(round, requirement.name))
            ],
            markers: requirement.nextRoundExpectedAt && requirement.nextRoundVehicle ? [
                {
                    id: `${requirement.seatKey}-next`,
                    at: fractionalYear(requirement.nextRoundExpectedAt),
                    kind: 'next-round',
                    title: `next round → ${requirement.nextRoundVehicle.name}`,
                    label: `next round → ${requirement.nextRoundVehicle.name}`
                }
            ] : []
        }));
    const scale = {
        start: year - 7,
        end: year + 3,
        now: data.now ? fractionalYear(data.now) : undefined
    };
    return /*#__PURE__*/ _jsxs("div", {
        ref: trackRef,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mb-2 flex justify-end",
                children: /*#__PURE__*/ _jsx(OpenAsLandscape, {
                    vehicleId: d.vehicleId
                })
            }),
            hatchVisible && orderingEnd && /*#__PURE__*/ _jsx(OrderingClosedOverlay, {
                host: host,
                scale: scale,
                orderingEnd: orderingEnd
            }),
            /*#__PURE__*/ _jsx(SpecraSwimlaneTimeline, {
                "data-testid": Testids.registryVehicle.roundsChart,
                title: title,
                density: "compact",
                formatTime: formatVehicleTime,
                renderLink: renderRouterLink,
                scale: scale,
                nowLabel: data.now ? `Now · ${monthYear(data.now)}` : undefined,
                periods: Array.from({
                    length: 10
                }, (_, i)=>({
                        id: String(year - 7 + i),
                        label: String(year - 7 + i),
                        start: year - 7 + i,
                        end: year - 6 + i
                    })),
                bands: [
                    {
                        id: 'rounds',
                        kind: 'lanes',
                        lanes
                    }
                ],
                legend: [
                    {
                        label: 'Us',
                        tone: 'ours'
                    },
                    {
                        label: 'Competitor',
                        tone: 'theirs'
                    },
                    {
                        label: 'Earlier holder',
                        tone: 'legacy'
                    },
                    {
                        label: 'Options / remaining PoP',
                        tone: 'theirs-future'
                    },
                    {
                        label: 'Our remaining PoP',
                        tone: 'ours-future'
                    },
                    ...hatchVisible && orderingEnd ? [
                        {
                            label: 'Ordering closed',
                            tone: 'other',
                            shape: 'outline'
                        }
                    ] : []
                ],
                insight: orderingEnd ? `Ordering on ${d.name} ends ${monthYear(orderingEnd)}${hatchVisible ? ' (hatched)' : ''}.${successor ? ` Every seat that comes up after that moves to ${successor.name}, so who holds ${successor.name} decides who can bid.` : ''}` : undefined,
                table: {
                    columns: [
                        {
                            key: 'seat',
                            label: 'Task order'
                        },
                        {
                            key: 'customer',
                            label: 'Customer'
                        },
                        {
                            key: 'value',
                            label: 'Award value'
                        },
                        {
                            key: 'holder',
                            label: 'Holder / period'
                        },
                        {
                            key: 'start',
                            label: 'From'
                        },
                        {
                            key: 'end',
                            label: 'To'
                        }
                    ],
                    rows: data.requirements.flatMap((r)=>{
                        const lane = lanes.find((l)=>l.id === r.seatKey);
                        return (lane?.segments ?? []).map((segment)=>({
                                id: `${r.seatKey}:${segment.id}`,
                                cells: {
                                    seat: r.name,
                                    customer: r.customer?.name,
                                    value: r.valueLabel,
                                    holder: segment.label,
                                    start: formatVehicleTime(segment.start),
                                    end: formatVehicleTime(segment.end)
                                }
                            }));
                    })
                },
                emptyMessage: "No task orders tracked on this vehicle yet."
            })
        ]
    });
}
